/**
 * Iter 248 — Hook Voice Activity Detection (VAD) basé sur Web Audio API.
 *
 * Utilise AnalyserNode + RMS pour détecter quand l'utilisateur parle ou se tait.
 * Quand un silence > silenceMs est détecté APRÈS une période de parole, déclenche
 * le callback `onPhraseEnd(blob)` avec l'audio enregistré (audio/webm).
 *
 * Configuration :
 * - threshold : 0.01-0.05 (RMS level pour considérer "parle")
 * - silenceMs : 800-1500ms (durée silence avant fin de phrase)
 * - minSpeechMs : 300ms (durée minimale de parole avant valide)
 *
 * Retour : { start, stop, isRecording, isSpeaking }
 */
import { useRef, useState, useCallback } from "react";

export function useVoiceActivityDetection({
  threshold = 0.015,
  silenceMs = 1000,
  minSpeechMs = 400,
  onPhraseEnd,
  audioSource = "microphone", // "microphone" | "tab" | { deviceId: "..." }
} = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const animFrameRef = useRef(null);

  // VAD state
  const speakingRef = useRef(false);
  const silenceStartRef = useRef(null);
  const speechStartRef = useRef(null);

  const stop = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) {}
      audioCtxRef.current = null;
    }
    setIsRecording(false);
    setIsSpeaking(false);
    speakingRef.current = false;
    silenceStartRef.current = null;
    speechStartRef.current = null;
  }, []);

  const flushPhrase = useCallback(() => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;
    // We stop the current recorder; onstop will fire with the accumulated blob
    try { mediaRecorderRef.current.stop(); } catch (e) {}
  }, []);

  const startNewRecorder = useCallback(() => {
    if (!streamRef.current) return;
    try {
      const mr = new MediaRecorder(streamRef.current, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          // Only fire phrase-end callback if we accumulated >= 500 bytes (filter noise)
          if (blob.size > 1000 && typeof onPhraseEnd === "function") {
            onPhraseEnd(blob);
          }
        }
        chunksRef.current = [];
        // Restart a fresh recorder so the next phrase can be captured
        if (streamRef.current && audioCtxRef.current) {
          startNewRecorder();
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
    } catch (e) {
      console.error("MediaRecorder error", e);
    }
  }, [onPhraseEnd]);

  const start = useCallback(async (overrideSource) => {
    if (isRecording) return;
    const effectiveSource = overrideSource !== undefined ? overrideSource : audioSource;
    try {
      // Acquire stream depending on audio source
      let stream;
      if (effectiveSource === "tab") {
        // B1 — Capture audio from a shared browser tab/window (the contact's voice)
        // The user picks which tab/window to share (Aircall web, Zoom, Teams, etc).
        // We don't care about video — we strip it after.
        if (!navigator.mediaDevices.getDisplayMedia) {
          throw new Error("Votre navigateur ne supporte pas la capture d'onglet. Utilisez Chrome/Edge.");
        }
        const ds = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
        // Validate that the user actually shared audio
        if (ds.getAudioTracks().length === 0) {
          ds.getTracks().forEach((t) => t.stop());
          throw new Error("Vous devez cocher 'Partager l'audio' dans la boîte de dialogue.");
        }
        // Build an audio-only stream
        stream = new MediaStream(ds.getAudioTracks());
        // Stop the video track (we don't need it) but keep audio
        ds.getVideoTracks().forEach((t) => t.stop());
      } else if (effectiveSource && typeof effectiveSource === "object" && effectiveSource.deviceId) {
        // B2 — Capture from a specific input device (e.g. Virtual Audio Cable)
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: effectiveSource.deviceId }, echoCancellation: false },
        });
      } else {
        // Default — microphone
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      streamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;

      startNewRecorder();
      setIsRecording(true);

      const buf = new Uint8Array(analyser.fftSize);
      const tick = () => {
        if (!analyserRef.current) return;
        analyser.getByteTimeDomainData(buf);
        // Compute RMS in [0,1]
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        const above = rms > threshold;
        const now = Date.now();

        if (above) {
          // Speaking
          if (!speakingRef.current) {
            speakingRef.current = true;
            speechStartRef.current = now;
            setIsSpeaking(true);
          }
          silenceStartRef.current = null;
        } else if (speakingRef.current) {
          // Silence after speech
          if (silenceStartRef.current === null) {
            silenceStartRef.current = now;
          } else if (now - silenceStartRef.current >= silenceMs) {
            // End of phrase detected
            const speechDuration = silenceStartRef.current - (speechStartRef.current || 0);
            if (speechDuration >= minSpeechMs) {
              flushPhrase();
            } else {
              // Too short, discard by stopping/restarting recorder silently
              if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                chunksRef.current = []; // drop chunks before restart
                try { mediaRecorderRef.current.stop(); } catch (e) {}
              }
            }
            speakingRef.current = false;
            speechStartRef.current = null;
            silenceStartRef.current = null;
            setIsSpeaking(false);
          }
        }
        animFrameRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      console.error("VAD start failed", e);
      throw e;
    }
  }, [isRecording, threshold, silenceMs, minSpeechMs, flushPhrase, startNewRecorder]);

  return { start, stop, isRecording, isSpeaking };
}
